import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useDelayedUnmount } from '../../../hooks/useDelayedUnmount';

/**
 * 资产变体选择器
 * 用于在分镜中选择使用资产的主体图还是某个变体
 *
 * @param {Object} props
 * @param {Object} props.asset - 资产对象（包含 id, name, imageUrl, variations）
 * @param {number} props.selectedVariantIndex - 当前选中的变体索引（-1=主体图，0+=变体）
 * @param {Function} props.onChange - 变体索引改变时的回调 (newIndex) => void
 * @param {string} props.size - 尺寸 'sm' | 'md' | 'lg'
 */
const AssetVariantSelector = ({ asset, selectedVariantIndex = -1, onChange, size = 'md' }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const buttonRef = React.useRef(null);
  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);

  if (!asset) return null;

  const variations = asset.variations || [];
  const hasVariations = variations.length > 0;

  // 获取当前选中的图片和名称
  const getCurrentImage = () => {
    if (selectedVariantIndex === -1) {
      return { url: asset.imageUrl, name: asset.name };
    }
    if (selectedVariantIndex >= 0 && selectedVariantIndex < variations.length) {
      const variant = variations[selectedVariantIndex];
      return { url: variant.imageUrl, name: `${asset.name} (${variant.title})` };
    }
    // 回退到主体图
    return { url: asset.imageUrl, name: asset.name };
  };

  const current = getCurrentImage();

  // 尺寸配置
  const sizeConfig = {
    sm: { image: 'w-8 h-8', text: 'text-2xs', dropdown: 'w-48' },
    md: { image: 'w-12 h-12', text: 'text-xs', dropdown: 'w-56' },
    lg: { image: 'w-16 h-16', text: 'text-sm', dropdown: 'w-64' }
  };
  const config = sizeConfig[size];

  const handleSelect = (index) => {
    onChange(index);
    setIsOpen(false);
  };

  const handleToggle = (e) => {
    if (!hasVariations) return;

    // 阻止事件冒泡，避免触发父元素的点击事件
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect(rect);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block">
      {/* 触发按钮 */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all ${
          hasVariations ? 'cursor-pointer hover:border-current' : 'cursor-default'
        }`}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: isOpen ? theme.colors.accentPrimary : 'var(--color-border-primary)',
          color: 'var(--color-text-primary)'
        }}
        disabled={!hasVariations}
      >
        {/* 缩略图 */}
        <div
          className={`${config.image} rounded overflow-hidden shrink-0`}
          style={{ backgroundColor: 'var(--color-bg-overlay)' }}
        >
          {current.url ? (
            <img src={current.url} alt={current.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={size === 'sm' ? 12 : size === 'md' ? 16 : 20} style={{ color: 'var(--color-text-muted)' }} />
            </div>
          )}
        </div>

        {/* 名称 */}
        <span className={`${config.text} font-medium truncate max-w-[120px]`}>
          {current.name}
        </span>

        {/* 下拉箭头（仅在有变体时显示） */}
        {hasVariations && (
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: 'var(--color-text-muted)' }}
          />
        )}
      </button>

      {/* 下拉菜单 - 使用 Portal 渲染到 body，避免被父容器遮挡 */}
      {mounted && hasVariations && ReactDOM.createPortal(
        <>
          {/* 遮罩层 */}
          <div
            className="fixed inset-0 z-[9997]"
            onClick={() => setIsOpen(false)}
          />

          {/* 菜单内容 */}
          <div
            className={`fixed ${config.dropdown} rounded-lg border shadow-lg z-[9998] overflow-hidden ${exiting ? "animate-out fade-out slide-out-to-top-2 duration-150" : "animate-in fade-in slide-in-from-top-2 duration-150"}`}
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border-primary)',
              top: `${buttonRect?.bottom + 4}px`,
              left: `${buttonRect?.left}px`
            }}
          >
            <div className="max-h-64 overflow-y-auto custom-scrollbar">
              {/* 主体图选项 */}
              <button
                onClick={() => handleSelect(-1)}
                className={`w-full flex items-center gap-2 px-3 py-2 transition-colors ${
                  selectedVariantIndex === -1 ? 'font-bold' : ''
                }`}
                style={{
                  backgroundColor: selectedVariantIndex === -1 ? `${theme.colors.accentPrimary}15` : 'transparent',
                  color: selectedVariantIndex === -1 ? theme.colors.accentPrimary : 'var(--color-text-primary)'
                }}
              >
                <div
                  className={`${config.image} rounded overflow-hidden shrink-0`}
                  style={{ backgroundColor: 'var(--color-bg-overlay)' }}
                >
                  {asset.imageUrl ? (
                    <img src={asset.imageUrl} alt={asset.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={size === 'sm' ? 12 : size === 'md' ? 16 : 20} style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className={`${config.text} truncate`}>{asset.name}</div>
                  <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>主体图</div>
                </div>
                {selectedVariantIndex === -1 && (
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: theme.colors.accentPrimary }} />
                )}
              </button>

              {/* 变体选项 */}
              {variations.map((variant, index) => (
                <button
                  key={index}
                  onClick={() => handleSelect(index)}
                  className={`w-full flex items-center gap-2 px-3 py-2 transition-colors ${
                    selectedVariantIndex === index ? 'font-bold' : ''
                  }`}
                  style={{
                    backgroundColor: selectedVariantIndex === index ? `${theme.colors.accentPrimary}15` : 'transparent',
                    color: selectedVariantIndex === index ? theme.colors.accentPrimary : 'var(--color-text-primary)'
                  }}
                >
                  <div
                    className={`${config.image} rounded overflow-hidden shrink-0`}
                    style={{ backgroundColor: 'var(--color-bg-overlay)' }}
                  >
                    {variant.imageUrl ? (
                      <img src={variant.imageUrl} alt={variant.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={size === 'sm' ? 12 : size === 'md' ? 16 : 20} style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className={`${config.text} truncate`}>{variant.title}</div>
                    {variant.description && (
                      <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                        {variant.description}
                      </div>
                    )}
                  </div>
                  {selectedVariantIndex === index && (
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: theme.colors.accentPrimary }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default AssetVariantSelector;
